import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ProcessorDetails } from '@/lib/api/processors'
import { formatMoney } from "@/lib/money"

interface NMIConfigurationCardProps {
    processor: ProcessorDetails
}

const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const getUsername = (options: Record<string, unknown> | null) => {
    return options?.username as string | undefined
}

const getNmiProcessorId = (options: Record<string, unknown> | null) => {
    return options?.nmiProcessorId as string | undefined
}

const getUseProcessorToken = (options: Record<string, unknown> | null) => {
    return options?.useProcessorToken === true
}

const getEnable3DS = (options: Record<string, unknown> | null) => {
    return options?.enable3DS === true
}

export const NMIConfigurationCard = ({ processor }: NMIConfigurationCardProps) => {
    const { t } = useTranslation('processors')
    const username = getUsername(processor.options)
    const useProcessorToken = getUseProcessorToken(processor.options)
    const nmiProcessorId = getNmiProcessorId(processor.options)
    const enable3DS = getEnable3DS(processor.options)

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>{t('nmiConfiguration')}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <p className="text-sm text-muted-foreground">{t('username')}</p>
                        <p className="font-mono text-sm">{username || t('notConfigured')}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('apiKey')}</p>
                        <p className="font-mono text-sm">••••••••••••••••</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('nmiProcessorId')}</p>
                        <p className="font-mono text-sm">{nmiProcessorId || t('notConfigured')}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('customerVault')}</p>
                        <Badge variant={useProcessorToken ? 'default' : 'secondary'}>
                            {useProcessorToken ? t('enabled') : t('disabled')}
                        </Badge>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('universal3DS')}</p>
                        <Badge variant={enable3DS ? 'default' : 'secondary'}>
                            {enable3DS ? t('enabled') : t('disabled')}
                        </Badge>
                    </div>

                    {processor.baseCurrency && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('baseCurrency')}</p>
                            <p className="font-semibold">{processor.baseCurrency}</p>
                        </div>
                    )}

                    {processor.monthlyProcessingCapacity !== null && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('monthlyCapacity')}</p>
                            <p className="font-semibold">
                                {formatMoney(processor.monthlyProcessingCapacity, processor.baseCurrency || 'USD')}
                            </p>
                        </div>
                    )}

                    <div>
                        <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
                        <p className="font-semibold">{formatDate(processor.createdAt)}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('updatedAt')}</p>
                        <p className="font-semibold">{formatDate(processor.updatedAt)}</p>
                    </div>

                    {processor.supportedCurrencies && processor.supportedCurrencies.length > 0 && (
                        <div className="col-span-full">
                            <p className="text-sm text-muted-foreground mb-2">{t('supportedCurrencies')}</p>
                            <div className="flex flex-wrap gap-2">
                                {processor.supportedCurrencies.map((currency) => (
                                    <Badge key={currency} variant="outline">
                                        {currency}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}

